import { useContext } from 'react';
import type { AttributionTokensSchema } from 'src/models/attributionTokens/schema';
import { initTokens } from 'src/providers/Browser';
import { BrowserCacheContext } from 'src/stores/BrowserCache';

export const useAttributionTokens = () => {
  const { cachedAttributionTokens } = useContext(BrowserCacheContext);

  const getTokens = (): AttributionTokensSchema | undefined => cachedAttributionTokens?.get();

  const setToken = (key: keyof AttributionTokensSchema, value: AttributionTokensSchema[typeof key]) => {
    const tokens = getTokens();
    cachedAttributionTokens?.set({ ...tokens, [key]: value });
  };

  const clearAllTokens = () => {
    cachedAttributionTokens?.set(initTokens);
  };
  const clearSearchToken = () => setToken('search', undefined);

  const clearSuggestToken = () => setToken('suggest', undefined);

  const clearRecommendToken = () =>
    setToken('recommend', { detailPageView: undefined, shoppingCartPageView: undefined });

  const updateSearchToken = (value: AttributionTokensSchema['search']) => {
    const tokens = getTokens();
    cachedAttributionTokens?.set({ ...tokens, search: value });
  };

  const updateSuggestToken = (value: AttributionTokensSchema['suggest']) => {
    const tokens = getTokens();
    cachedAttributionTokens?.set({ ...tokens, suggest: value });
  };

  const updateRecommendToken = (value: Partial<AttributionTokensSchema['recommend']>) => {
    const currentTokens = getTokens();
    const updatedRecommend = { ...currentTokens?.recommend, ...value };
    setToken('recommend', updatedRecommend);
  };

  return {
    getTokens,
    clearAllTokens,
    clearSearchToken,
    clearSuggestToken,
    clearRecommendToken,
    updateSearchToken,
    updateSuggestToken,
    updateRecommendToken
  };
};

enum AttributionPath {
  cartPage = 'cart',
  searchPage = 'search',
  detailPage = 'product'
}

export const getTargetToken = (tokens: AttributionTokensSchema | undefined, prevPath: string | null) => {
  if (!tokens || !prevPath) return;

  if (prevPath.includes(AttributionPath.cartPage)) {
    return tokens.recommend?.shoppingCartPageView;
  } else if (prevPath.includes(AttributionPath.searchPage)) {
    return tokens.search;
  } else if (prevPath.includes(AttributionPath.detailPage)) {
    return tokens.recommend?.detailPageView;
  }
};
